import * as z from "zod";
import { CUSTOM_ANALYTICS_QUERY_PLAN_DEFAULT_OCCURRENCE } from "../../../semantic-queries/v1/query-plan/custom-analytics-query-plan-constants";
import type { ValueOf } from "../analytics-grains/analytics-grain-types";
import type { SemanticTableName } from "../semantic-catalog-refs";
import type { AnalyticsRelationshipName } from "./analytics-grain-relationship-constants";

export const AnalyticsRelationshipRouteIntent = {
  Pin: "pin",
  Override: "override",
  Meaning: "meaning",
} as const;

export type AnalyticsRelationshipRouteIntent = ValueOf<
  typeof AnalyticsRelationshipRouteIntent
>;

const AnalyticsRelationshipMeaningNameRegex = /^[a-z][a-z0-9_]*$/;

export const AnalyticsRelationshipMeaningNameSchema = z
  .string()
  .regex(AnalyticsRelationshipMeaningNameRegex)
  .max(24)
  .refine((name) => name !== CUSTOM_ANALYTICS_QUERY_PLAN_DEFAULT_OCCURRENCE, {
    message: `A relationship meaning becomes a join-site occurrence, which reserves "${CUSTOM_ANALYTICS_QUERY_PLAN_DEFAULT_OCCURRENCE}" for absence.`,
  });

export type AnalyticsRelationshipMeaningName = string;

export interface AnalyticsRelationshipMeaningDefinition {
  readonly name: AnalyticsRelationshipMeaningName;
  readonly shortLabel?: string;
}

/**
 * GLOBAL append-only registry: a name in a stored AST cannot be withdrawn.
 * A new meaning answers "which one?" about the target: never a table or join.
 */
export const AnalyticsRelationshipMeaning = {
  Primary: { name: "primary" },
  ExchangeOrders: { name: "exchange_orders", shortLabel: "exchange order" },
  ExchangeSource: { name: "exchange_source", shortLabel: "exchanged from" },
  OrderCharges: { name: "order_charges", shortLabel: "order charges" },
  ViaOrderLineItem: {
    name: "via_order_line_item",
    shortLabel: "via order line item",
  },
} as const satisfies Record<string, AnalyticsRelationshipMeaningDefinition>;

export const AnalyticsRelationshipMeaningDefinitions: readonly AnalyticsRelationshipMeaningDefinition[] =
  Object.values(AnalyticsRelationshipMeaning);

export function analyticsRelationshipMeaningDefinition(
  meaningName: AnalyticsRelationshipMeaningName,
): AnalyticsRelationshipMeaningDefinition | undefined {
  return AnalyticsRelationshipMeaningDefinitions.find(
    (definition) => definition.name === meaningName,
  );
}

export function analyticsRelationshipMeaningShortLabel(
  meaningName: AnalyticsRelationshipMeaningName,
): string | undefined {
  return analyticsRelationshipMeaningDefinition(meaningName)?.shortLabel;
}

/** Absence means the primary route: the one the unpinned search returns. */
export function analyticsRelationshipMeaningOrPrimary(
  meaningName: AnalyticsRelationshipMeaningName | undefined,
): AnalyticsRelationshipMeaningName {
  return meaningName ?? AnalyticsRelationshipMeaning.Primary.name;
}

export function isPrimaryAnalyticsRelationshipMeaning(
  meaningName: AnalyticsRelationshipMeaningName | undefined,
): boolean {
  return (
    analyticsRelationshipMeaningOrPrimary(meaningName) ===
    AnalyticsRelationshipMeaning.Primary.name
  );
}

/**
 * The meanings an authoring channel may name: registered AND declared by a pin, so
 * an advertised meaning always resolves. Empty means the meaning surfaces stay dark.
 */
export function declaredAnalyticsRelationshipMeaningNames(
  routePins: readonly AnalyticsRelationshipRoutePin<string, string, string>[],
): readonly AnalyticsRelationshipMeaningName[] {
  return [
    ...new Set(
      routePins.flatMap((pin) =>
        pin.meaningName &&
        !isPrimaryAnalyticsRelationshipMeaning(pin.meaningName) &&
        analyticsRelationshipMeaningDefinition(pin.meaningName)
          ? [pin.meaningName]
          : [],
      ),
    ),
  ].sort();
}

export interface AnalyticsRelationshipRoutePin<
  TFromTable extends string = SemanticTableName,
  TToTable extends string = SemanticTableName,
  TName extends string = AnalyticsRelationshipName,
> {
  readonly from: TFromTable;
  readonly to: TToTable;
  readonly meaningName?: AnalyticsRelationshipMeaningName;
  readonly via: readonly TName[];
  readonly intent: AnalyticsRelationshipRouteIntent;
  readonly meaning: string;
  readonly ratherThan: string;
}

/** A requested route is not declared or reachable; user-correctable, so classified by type not prose. */
export class AnalyticsRelationshipRouteUnavailableError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "AnalyticsRelationshipRouteUnavailableError";
  }
}

export function findAnalyticsRelationshipRoutePin<
  TTable extends string,
  TName extends string,
>({
  routePins,
  from,
  to,
  meaningName,
}: {
  readonly routePins: readonly AnalyticsRelationshipRoutePin<
    TTable,
    TTable,
    TName
  >[];
  readonly from: TTable;
  readonly to: TTable;
  readonly meaningName?: AnalyticsRelationshipMeaningName;
}): AnalyticsRelationshipRoutePin<TTable, TTable, TName> | undefined {
  const requested = analyticsRelationshipMeaningOrPrimary(meaningName);
  return routePins.find(
    (pin) =>
      pin.from === from &&
      pin.to === to &&
      analyticsRelationshipMeaningOrPrimary(pin.meaningName) === requested,
  );
}
